// src/pages/Rooms.js
import React, { useState } from 'react';
import images from '../assets/images';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  styled,
} from '@mui/material';

const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: '100%',
  margin: 'auto',
  boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
  borderRadius: '16px',
  overflow: 'hidden',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-10px)',
  },
}));

const rooms = [
  {
    id: 1,
    title: 'Garden Room',
    type: 'Deluxe',
    price: 2500,
    size: '45 sq.m',
    capacity: '2 Adults, 1 Child',
    image: images.main,
    description: 'Experience tranquility in our Garden Villa room. Wake up to serene views of lush greenery and enjoy modern comforts in a spacious setting designed for your perfect getaway. Our thoughtfully designed rooms blend natural beauty with contemporary amenities to create your home away from home',
    amenities: [
      'King Size Bed',
      'Garden View',
      'Private Balcony',
      'Free Wi-Fi',
      'Room Service',
      'Air Conditioning',
      'TV',
    ],
  },
];

function RoomDialog({ room, open, onClose }) {
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          p: 2,
        }
      }}
    >
      <DialogTitle sx={{ fontSize: '2rem', fontWeight: 600 }}>
        {room.title}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={room.image}
              alt={room.title}
              sx={{
                width: '100%',
                height: '400px',
                objectFit: 'cover',
                borderRadius: '12px',
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
              Room Details
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
              Size: {room.size}
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
              Capacity: {room.capacity}
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mt: 3 }}>
              Amenities
            </Typography>
            <Grid container spacing={2}>
              {room.amenities.map((amenity) => (
                <Grid item xs={6} key={amenity}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      '&:before': {
                        content: '"•"',
                        color: 'primary.main',
                        mr: 1,
                        fontWeight: 'bold',
                      }
                    }}
                  >
                    {amenity}
                  </Typography>
                </Grid>
              ))}
            </Grid>
            <Typography variant="h4" color="primary" sx={{ mt: 4, fontWeight: 600 }}>
            ₹{room.price} / night
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={onClose} 
          variant="outlined" 
          size="large"
          sx={{ borderRadius: '30px', px: 4 }}
        >
          Close
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          href="/booking"
          size="large"
          sx={{ 
            borderRadius: '30px', 
            px: 4,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(46, 125, 50, 0.25)',
            },
          }}
        >
          Book
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Rooms() {
  const [selectedRoom, setSelectedRoom] = useState(null);

  return (
    <Box sx={{ py: 12, minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
      <Container maxWidth="lg">
        <Typography 
          variant="h3" 
          component="h1" 
          textAlign="center" 
          gutterBottom
          sx={{ 
            mb: 8,
            fontWeight: 600,
            '&:after': {
              content: '""',
              display: 'block',
              width: '80px',
              height: '3px',
              backgroundColor: 'primary.main',
              margin: '15px auto',
            }
          }}
        >
          Our Room
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Grid container justifyContent="center">
            <Grid item xs={12} sm={10} md={8}>
              <StyledCard>
                <CardMedia
                  component="img"
                  height="400"
                  image={rooms[0].image}
                  alt={rooms[0].title}
                />
                <CardContent sx={{ p: 4 }}>
                  <Typography 
                    gutterBottom 
                    variant="h4" 
                    component="h2"
                    sx={{ fontWeight: 600 }}
                  >
                    {rooms[0].title}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="text.secondary" 
                    paragraph
                    sx={{ fontSize: '1.1rem', mb: 3 }}
                  >
                    {rooms[0].description}
                  </Typography>
                  <Typography 
                    variant="h5" 
                    color="primary" 
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 3 }}
                  >
                    Weekdays: ₹ {rooms[0].price} / night Weekends: ₹ 3000 / night
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                      variant="outlined" 
                      color="primary"
                      onClick={() => setSelectedRoom(rooms[0])}
                      size="large"
                      sx={{ 
                        flex: 1,
                        borderRadius: '30px',
                        py: 1.5,
                      }}
                    >
                      View Details
                    </Button>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      href="/booking"
                      size="large"
                      sx={{ 
                        flex: 1,
                        borderRadius: '30px',
                        py: 1.5,
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 20px rgba(46, 125, 50, 0.25)',
                        },
                      }}
                    >
                      Book Now
                    </Button>
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>
          </Grid>
        </Box>

        {selectedRoom && (
          <RoomDialog
            room={selectedRoom}
            open={Boolean(selectedRoom)}
            onClose={() => setSelectedRoom(null)}
          />
        )}
      </Container>
    </Box>
  );
}

export default Rooms;